import React, { useEffect, useState } from "react";
import { Button, Table, Whisper, Popover, Badge } from "rsuite";
import styled, { css, createGlobalStyle } from "styled-components";

import {
    REGISTRATION_REQUEST_STATUS_APPROVED,
    REGISTRATION_REQUEST_STATUS_REJECTED,
    REGISTRATION_REQUEST_STATUS_SENT
} from "const";
import { ServiceLink } from "components/Navigation/ServiceLink";
import BaseTable from "components/base/BaseTable";
import { Icon } from "components/Icon";
import { selectableFields } from "../Login/RegistrationFormFields";
import ModalRejectRegistration from "./ModalRejectRegistration";

const { Cell, Column, HeaderCell } = Table;

const ipResistrationStatuses = {
    0: "New",
    1: "Created",
    2: "Rejected"
};

export default ({
    dataList,
    filter,
    currencyList,
    onSetRedirectTab,
    rejectRegistration,
    getRegistrationList,
    getRegistrationCount,
    setDefaultFormCreateAccount,
    loading,
}) => {

    const [dataListState, onChangeDataList] = useState(dataList);
    const allRequestStatusList = [
        REGISTRATION_REQUEST_STATUS_SENT,
        REGISTRATION_REQUEST_STATUS_APPROVED,
        REGISTRATION_REQUEST_STATUS_REJECTED
    ];

    useEffect(() => {
        if (!filter)
            onChangeDataList(dataList);

        if (!filter.processed) {
            onChangeDataList(dataList.filter(value => value.status === REGISTRATION_REQUEST_STATUS_SENT));
        } else if (filter.processed) {
            onChangeDataList(dataList.filter(value => allRequestStatusList.includes(value.status)));
        }
    }, [dataList, filter.processed]);

    const columns = [
        {
            flexGrow: 2,
            label: "Name",
            value: ({ name }) => {
                return <span>{name}</span>;
            }
        },
        {
            flexGrow: 3,
            label: "Contacts",
            value: ({ email, teams_email, teams_link }) => {
                const teamsLink = teams_link ? <a target="_blank" href={teams_link}>Teams link</a> : null;

                return <span>
                    {getColumnKeyValueString({ "Email": email, "Teams email": teams_email, "Teams link": teamsLink }, true)}
                </span>;
            }
        },
        {
            flexGrow: 3,
            label: "Detail",
            value: ({ cntr_code, cur_key, month_traffic_minute, traffic_type, comment }) => {
                let currencyName = cur_key;
                if (cur_key) {
                    const dataListValue = currencyList.find(value => value["cur_key"] === cur_key);
                    currencyName = dataListValue && dataListValue.name;
                }

                let trafficTypeValue = traffic_type;
                if (traffic_type !== null && traffic_type.toString()) {
                    const dataListValue = selectableFields["select_traffic_type"].find(value => {
                        return value.id === trafficTypeValue;
                    });
                    trafficTypeValue = dataListValue && dataListValue.name;
                }
                return <span>
                    {getColumnKeyValueString({ "Country": cntr_code, "Currency": currencyName, "Monthly traffic": month_traffic_minute, "Traffic type": trafficTypeValue, "How found": comment })}
                </span>;
            }
        },
        {
            flexGrow: 2,
            label: "Comment",
            value: ({ rejected_at, rejected_manager_name, account }) => {
                const accountId = account?.id;
                const accountName = account?.name;
                const existingLink = `/accounts/view/${accountId}`;
                const linkedAccountName = accountName ? <ServiceLink to={existingLink} onClick={() => { onSetRedirectTab("general") }}>{accountName}</ServiceLink> : null;
                const date = createFormatDate(rejected_at);
                return <span>
                    {getColumnKeyValueString(
                        {
                            "Email already exist in": linkedAccountName,
                            "rejected at": date,
                            "manager": rejected_manager_name
                        }
                    )}
                </span>;
            }
        },
        {
            flexGrow: 1,
            label: "Service",
            value: ({ voice, sms }) => {
                const services = `${voice && sms ? "Voice, SMS" : voice && !sms ? "Voice" : !voice && sms ? "SMS" : ""}`;
                return services;
            },
            minWidth: 120,
        },
        {
            flexGrow: 1,
            label: "Created at",
            value: ({ created_at }) => {
                const date = createFormatDate(created_at);
                return <span>{date}</span>;
            }
        },
        {
            flexGrow: 1,
            label: "IP",
            value: ({ ip, ip_registrations, ip_registration_list }) => {
                return <IpRegistrations>
                    {ip} {ip_registrations ? <Whisper
                        trigger="hover"
                        placement="bottomEnd"
                        speaker={<Popover>
                            <table>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                </tr>
                                {ip_registration_list.map((item) => {
                                    return <tr>
                                        <td>{item?.name}</td>
                                        <td>{item?.email}</td>
                                        <td>{createFormatDate(item?.created_at)}</td>
                                        <td>{ipResistrationStatuses[item?.status]}</td>
                                    </tr>
                                })}
                            </table>
                        </Popover>}
                    >
                        <Badge content={ip_registrations} />
                    </Whisper> : null}
                </IpRegistrations>;
            },
            minWidth: 120,
            align: "left"
        }
    ];

    const [showRejectModal, setShowRejectModal] = useState(false);

    const fillZeroDateValue = (date) => {
        if (date.toString().length === 1) {
            return `0${date}`;
        }
        return date;
    };

    const getColumnKeyValueString = (keyValueObj, compact = false) => {
        return <StyledKeyValueColumn compact={compact}>
            {Object.keys(keyValueObj).map((key) => {
                if (keyValueObj[key]) {
                    return <StyledColumnItem><b>{key}</b>: {keyValueObj[key]}</StyledColumnItem>
                }
            })}
        </StyledKeyValueColumn>;
    };

    const createFormatDate = (data) => {
        const dateObject = new Date(data);
        const day = fillZeroDateValue(dateObject.getDate());
        const month = fillZeroDateValue(dateObject.getMonth() + 1);
        const year = fillZeroDateValue(dateObject.getFullYear() - 2000);
        const hours = fillZeroDateValue(dateObject.getHours());
        const minutes = fillZeroDateValue(dateObject.getMinutes());
        return data ? `${day}/${month}/${year} ${hours}:${minutes}` : null;
    };

    return (
        <>
            <GlobalStyle />
            <StyledTable
                virtualized
                className="registration"
                shouldUpdateScroll={false}
                headerHeight={46}
                rowHeight={68}
                height={68 * 8 + 46}
                // autoHeight
                rowClassName={(rowData) => {
                    if (rowData) {
                        let rowClassName = "registration_column sent";

                        if (rowData.status === REGISTRATION_REQUEST_STATUS_APPROVED) {
                            rowClassName = "registration_column approved";
                        } else if (rowData.status === REGISTRATION_REQUEST_STATUS_REJECTED) {
                            rowClassName = "registration_column rejected";
                        }

                        return rowClassName;
                    }
                }}
                data={dataListState}
                loading={loading}
            >
                {columns.map(({
                    dataKey, dataValue, flexGrow = null, minWidth = 130, label = "", data, accepter, valueKey, type,
                    date, tooltip, value, align = "left", verticalAlign = "middle", ...columnProps
                }) => {
                    return (<Column {...{ flexGrow, minWidth, align, verticalAlign, ...columnProps }}>
                        <HeaderCell>
                            <span className="registration_text_field">{label}</span>
                        </HeaderCell>
                        <Cell {...{ dataKey }}>
                            {value}
                        </Cell>
                    </Column>);
                })}
                <Column width={90} align="left" verticalAlign="middle">
                    <HeaderCell></HeaderCell>
                    <Cell>
                        {(rowData) => {
                            let requestAction = <div className="registration_buttons_field">
                                <ServiceLink to="/accounts/new">
                                    <Button
                                        disabled={
                                            rowData.status !== REGISTRATION_REQUEST_STATUS_SENT ||
                                            rowData.account_id && rowData.status === REGISTRATION_REQUEST_STATUS_APPROVED ||
                                            !!rowData.account_id
                                        }
                                        className="registration_button approve"
                                        appearance="primary"
                                        color="green"
                                        onClick={() => {
                                            setDefaultFormCreateAccount({
                                                name: rowData.name,
                                                email: rowData.email,
                                                teams_email: rowData.teams_email,
                                                teams_link: rowData.teams_link,
                                                cur_key: rowData.cur_key,
                                                reg_key: rowData.reg_key,
                                                hf_key: rowData.hf_key,
                                                cntr_code: rowData.cntr_code,
                                            });
                                        }}
                                    >
                                        <Icon icon="plus-square" />
                                    </Button>
                                </ServiceLink>
                                <Button
                                    disabled={rowData.status !== REGISTRATION_REQUEST_STATUS_SENT}
                                    className="registration_button reject"
                                    appearance="primary"
                                    color="red"
                                    onClick={() => {
                                        setShowRejectModal(rowData["reg_key"]);
                                    }}
                                >
                                    <Icon icon="warning" />
                                </Button>
                            </div>;

                            return requestAction;
                        }}
                    </Cell>
                </Column>
            </StyledTable>
            <ModalRejectRegistration
                show={showRejectModal}
                onSubmit={() => {
                    rejectRegistration({ reg_key: showRejectModal }).then(() => {
                        setShowRejectModal(null);
                        getRegistrationList();
                        getRegistrationCount();
                    });
                }}
                onClose={() => setShowRejectModal(null)}
            />
        </>
    );
};

const StyledTable = styled(BaseTable)`
    && {
        .registration_button {
            width: 30px;
            height: 30px;
            padding: 5px;
        }

        .registration_button.approve:not(:disabled):not(.rs-btn-disabled) {
            background-color: var(--rs-color-green, #4caf50) !important;
            color: #fff !important;
        }

        .registration_button.approve:not(:disabled):not(.rs-btn-disabled):hover,
        .registration_button.approve:not(:disabled):not(.rs-btn-disabled):focus {
            background-color: var(--rs-green-600, #43a047) !important;
            color: #fff !important;
        }

        .registration_button.reject:not(:disabled):not(.rs-btn-disabled) {
            background-color: var(--rs-color-red, #f44336) !important;
            color: #fff !important;
        }

        .registration_button.reject:not(:disabled):not(.rs-btn-disabled):hover,
        .registration_button.reject:not(:disabled):not(.rs-btn-disabled):focus {
            background-color: var(--rs-red-600, #e53935) !important;
            color: #fff !important;
        }

        .registration_text_field {
            display: block;
            line-height: 20px;
            word-break: normal;
        }

        .registration_column.approved, .registration_column.approved .rs-table-cell {
            background-color: #dfffdf !important;
        }
        .registration_column.rejected, .registration_column.rejected .rs-table-cell {
            background-color: #e2e2e2 !important;
        }
        .registration_buttons_field {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 5px;
        }
        .created_account_link {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }
        .existing_account_buttons_field {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }
        .rs-table-cell-content {
            padding: 6px 10px;
        }
    }
`;

const StyledKeyValueColumn = styled.div`
    line-break: auto;
    line-height: 20px;
    display: block;

    ${props => props.compact && css`
        line-height: 16px;
        font-size: 13px;
    `}
`;

const StyledColumnItem = styled.span`
    display: block;
    margin-right: 15px;
    float: left;
`;

const IpRegistrations = styled.span`
    && {
        .rs-badge {
            position: absolute;
            top: 20px;
        }

      
    }
`;

const GlobalStyle = createGlobalStyle`
    .rs-popover table tr td {
        padding-right: 8px;
    }
`;
